import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ApplicantDashboard from './ApplicantDashboard';
import HelportSupportAvatar from './HelportSupportAvatar';
import ChatToast from './ChatToast';
import { getT } from '../i18n';
import {
  collectChatFingerprint,
  countUnreadConversations,
  findNewChatAlerts,
} from '../utils/chatNotifications';
import {
  filterDeletedMessages,
  groupMessagesIntoConversations,
  isChatMessage,
} from '../utils/messageConversations';
import './ApplicantSupportWidget.css';

const ApplicantSupportWidget = ({
  isOpen,
  onToggle,
  onClose,
  lang = 'en',
  currentUser,
  messages = [],
  application = null,
  onReplyMessage,
  onEditChatMessage,
  onDeleteChatMessage,
  hiddenMessageIds,
  onGoToApply
}) => {
  const t = getT(lang);
  const ta = t.applicant;
  const [chatToast, setChatToast] = useState(null);
  const chatFingerprintRef = useRef(null);

  const applicantConversations = useMemo(() => {
    const email = String(currentUser?.email || '').trim().toLowerCase();
    const scoped = (messages || []).filter((msg) => {
      if (!isChatMessage(msg)) return false;
      if (!email) return true;
      return String(msg.email || '').trim().toLowerCase() === email;
    });
    return groupMessagesIntoConversations(filterDeletedMessages(scoped));
  }, [messages, currentUser?.email]);

  const unreadCount = useMemo(
    () => countUnreadConversations(applicantConversations, 'applicant'),
    [applicantConversations]
  );

  useEffect(() => {
    const nextFingerprint = collectChatFingerprint(applicantConversations);
    if (chatFingerprintRef.current === null) {
      chatFingerprintRef.current = nextFingerprint;
      return;
    }

    const alerts = findNewChatAlerts(chatFingerprintRef.current, applicantConversations, 'applicant');
    chatFingerprintRef.current = nextFingerprint;

    if (!alerts.length || isOpen) return;

    const latest = alerts[0];
    setChatToast({
      id: latest.id,
      title: ta.newAdminReplyTitle,
      body: latest.preview || ta.newAdminReplyBody,
    });
  }, [applicantConversations, isOpen, ta.newAdminReplyTitle, ta.newAdminReplyBody]);

  useEffect(() => {
    if (isOpen) setChatToast(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape, true);
    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  return createPortal(
    <div className="applicant-support-widget" aria-live="polite">
      <ChatToast
        open={Boolean(chatToast)}
        title={chatToast?.title}
        body={chatToast?.body}
        actionLabel={ta.waitingGoMessages || 'Open'}
        onAction={() => {
          setChatToast(null);
          onToggle();
        }}
        onClose={() => setChatToast(null)}
      />

      {isOpen && (
        <>
          <button
            type="button"
            className="applicant-support-backdrop"
            onClick={onClose}
            aria-label={t.common.close}
          />
          <div
            id="applicant-support-panel"
            className="applicant-support-panel"
            role="dialog"
            aria-modal="true"
            aria-label={ta.supportBotTitle}
          >
            <div className="applicant-support-panel-header">
              <div className="applicant-support-panel-heading">
                <span className="applicant-support-panel-icon" aria-hidden="true">
                  <HelportSupportAvatar alt={ta.supportBotTitle} />
                </span>
                <div>
                  <p className="applicant-support-panel-title">{ta.supportBotTitle}</p>
                  <p className="applicant-support-panel-status">
                    <span className="applicant-support-panel-dot" aria-hidden="true" />
                    {ta.supportBotOnline}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="applicant-support-panel-close"
                onClick={onClose}
                aria-label={t.common.close}
              >
                ×
              </button>
            </div>
            <div className="applicant-support-panel-body">
              <ApplicantDashboard
                variant="widget"
                lang={lang}
                currentUser={currentUser}
                messages={messages}
                application={application}
                onReplyMessage={onReplyMessage}
                onEditChatMessage={onEditChatMessage}
                onDeleteChatMessage={onDeleteChatMessage}
                hiddenMessageIds={hiddenMessageIds}
                onGoToApply={onGoToApply}
              />
            </div>
          </div>
          <button
            type="button"
            className="applicant-support-desktop-fab"
            onClick={onClose}
            aria-label={t.common.close}
          >
            <span className="applicant-support-launcher-icon" aria-hidden="true">
              <HelportSupportAvatar alt={ta.supportBotTitle} />
              <span className="applicant-support-launcher-dot" />
            </span>
          </button>
        </>
      )}

      {!isOpen && (
        <div className="applicant-support-stack">
          <button
            type="button"
            className={`applicant-support-launcher${unreadCount > 0 ? ' has-unread' : ''}`}
            onClick={onToggle}
            aria-expanded={false}
            aria-controls="applicant-support-panel"
          >
            <span className="applicant-support-launcher-icon" aria-hidden="true">
              <HelportSupportAvatar alt={ta.supportBotTitle} />
              <span className="applicant-support-launcher-dot" />
              {unreadCount > 0 && (
                <span className="applicant-support-unread-badge">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </span>
            <span className="applicant-support-launcher-text">
              <span className="applicant-support-launcher-title">{ta.supportBotTitle}</span>
              <span className="applicant-support-launcher-status">
                {unreadCount > 0 ? (ta.newAdminReplyTitle || ta.supportBotOnline) : ta.supportBotOnline}
              </span>
            </span>
          </button>
        </div>
      )}
    </div>,
    document.body
  );
};

export default ApplicantSupportWidget;
